import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaCog, FaChevronUp, FaChevronDown, FaTrash, FaCalendarAlt, FaCreditCard, FaImage } from "react-icons/fa";
import { refreshDataUserService, loggOutUser } from "../store/userSlice";
import SubscriptionManagerOneTimePayment from "./SubscriptionManagerOneTimePayment";
import UserService from "../services/userService";
import AdminService from "../services/adminService";
import LoadingForButton from "./LoadingForButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { FiX } from "react-icons/fi";
import NotificationBanner from "./NotificationBanner";

const daysOptions = [
    // { value: 0, labelKey: "onWeddingDay" },
    { value: 1, labelKey: "1dayBefore" },
    { value: 3, labelKey: "3daysBefore" },
    { value: 5, labelKey: "5daysBefore" },
    { value: 7, labelKey: "7daysBefore" },
    { value: 14, labelKey: "14daysBefore" },
];

const SettingsModal = ({ onClose, initialTab = "personal" }) => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.userStore.user);
    const wedding = useSelector((state) => state.userStore.wedding);
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(initialTab);
    const [showTabs, setShowTabs] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [errorResponse, setErrorResponse] = useState(false);
    const [invitationFile, setInvitationFile] = useState(null);
    const [invitationPreview, setInvitationPreview] = useState(wedding?.invitationUrl || null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteInvitationModal, setShowDeleteInvitationModal] = useState(false);

    const initialPersonal = {
        userName: user.userName || "",
        email: user.email || "",
        phone: user.phone || "",
        weddingName: wedding?.name || "",
        brideName: wedding?.brideName || "",
        groomName: wedding?.groomName || "",
        date: wedding?.date || "",
        location: wedding?.location || "",
        city: wedding?.city || "",
        lockReservationDaysBefore: wedding?.lockReservationDaysBefore || 3,
        dateChangeCount: wedding?.dateChangeCount || 0,
        inviteMessage: wedding?.inviteMessage || "Klikni na link i odaberi stol koji ti odgovara. Ako ne stigneš, ne brini — pobrinut ćemo se za tebe.",
        invitationUrl: wedding?.invitationUrl || null,
        allowGroupReservation: wedding?.allowGroupReservation ?? true,
    };

    const [formData, setFormData] = useState(initialPersonal);
    const [errors, setErrors] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [passwordErrors, setPasswordErrors] = useState({});
    const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [subLoading, setSubLoading] = useState(false);

    // Pretpostavimo da se iz backend-a dohvaća trenutni plan
    const currentSubscription = user.subscription || null;

    const plans = [
        {
            priceId: "price_1SG5pHHGuAO66F9rtJuSA68x",
            planType: "monthly",
            name: t("monthlyPlan"),
            price: "9.99 € / " + t("month"),
            description: t("monthlyDesc", "Idealno za kratkoročne događaje i testiranje funkcionalnosti."),
            duration: "1 " + t("month"),
        },
        {
            priceId: "price_1SNhGLHGuAO66F9r2I3Z41pd",
            planType: "yearly",
            name: t("yearlyPlan"),
            price: "99.99 € / " + t("months"),
            description: t("yearlyDesc", "Idealno za dugoročne događaje i prodaju."),
            duration: "12 " + t("months"),
        },
    ];

    const navigate = useNavigate();
    const lockReservationRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        const current = JSON.stringify(formData);
        const original = JSON.stringify(initialPersonal);
        setHasChanges(current !== original);
    }, [formData]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openDropdown === "lockReservation" && lockReservationRef.current && !lockReservationRef.current.contains(e.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    const scrollToDropdown = () => {
        if (lockReservationRef.current) {
            lockReservationRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center' // ili 'start' ili 'nearest'
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSaveUser = () => {
        const newErrors = {};
        // if (!formData.userName.trim()) newErrors.userName = t("fieldRequired");
        if (!formData.email.trim()) newErrors.email = t("fieldRequired");
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t("invalidEmail");

        if (Object.keys(newErrors).length) return setErrors(newErrors);

        setLoading(true);
        UserService.updateUser(user._id, {
            userName: formData.userName,
            email: formData.email,
            phone: formData.phone ?? ""
        })
            .then(() => {
                dispatch(refreshDataUserService());
                setLoading(false);
                onClose();
            })
            .catch((err) => {
                // console.log(err);
                setInvalidCredentials(true);
                setLoading(false);
            });
    };

    const handleSaveWedding = () => {
        const today = new Date().toISOString().split('T')[0];
        const cityRegex = /^[a-zA-ZšđčćžŠĐČĆŽ\s'-]+$/;
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

        const normalizeDateForBackend = (date) => {
            if (!date) return null;

            if (date instanceof Date) {
                return date.toISOString().split('T')[0];
            }

            if (typeof date === 'string') {
                if (date.includes('T')) {
                    return date.split('T')[0];
                }
                return date;
            }

            return null;
        };

        const normalizedDate = normalizeDateForBackend(formData.date);

        const newErrors = {};

        if (!formData.brideName.trim()) {
            newErrors.brideName = t("fieldRequired");
        } else if (formData.brideName.length > 50) {
            newErrors.brideName = t("maxLengthError", { count: 50 });
        }

        if (!formData.groomName.trim()) {
            newErrors.groomName = t("fieldRequired");
        } else if (formData.groomName.length > 50) {
            newErrors.groomName = t("maxLengthError", { count: 50 });
        }

        if (!formData.date) {
            newErrors.date = t('dateRequired');
        } else if (!dateRegex.test(normalizedDate)) {
            newErrors.date = t('dateInvalid');
        } else if (normalizedDate < today) {
            newErrors.date = t('dateInFuture');
        }

        if (!formData.location.trim()) {
            newErrors.location = t("fieldRequired");
        } else if (formData.location.length > 100) {
            newErrors.location = t("maxLengthError", { count: 100 });
        }

        if (!formData.city.trim()) {
            newErrors.city = t('cityRequired');
        } else if (!cityRegex.test(formData.city.trim())) {
            newErrors.city = t('cityInvalid');
        } else if (formData.city.trim().length < 3) {
            newErrors.city = t('cityMinLength');
        } else if (formData.city.trim().length > 100) {
            newErrors.city = t('maxLengthError', { count: 100 });
        }

        if (Object.keys(newErrors).length) return setErrors(newErrors);

        const sanitizedInviteMessage = formData.inviteMessage.trim().replace(/[^\S\n]+/g, ' ')        // zamijeni whitespace osim \n s jednim razmakom.replace(/[^\S\n]+([.,!?])/g, '$1');

        setLoading(true);
        AdminService.updateWedding(wedding._id, {
            name: formData.weddingName,
            brideName: formData.brideName,
            groomName: formData.groomName,
            date: normalizedDate,
            location: formData.location,
            city: formData.city,
            lockReservationDaysBefore: formData.lockReservationDaysBefore,
            inviteMessage: sanitizedInviteMessage,
            allowGroupReservation: formData.allowGroupReservation
        })
            .then(() => {
                dispatch(refreshDataUserService());
                setLoading(false);
                onClose();
            })
            .catch(() => setLoading(false));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...passwordData, [name]: value };
        setPasswordData(updated);
        if (submitted) setPasswordErrors(validatePassword(updated));
    };

    const validatePassword = (data = passwordData) => {
        const errs = {};
        const { currentPassword, newPassword, confirmPassword } = data;
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!currentPassword) errs.currentPassword = t("fieldRequired");
        if (!newPassword || !regex.test(newPassword)) errs.newPassword = t("passwordTooWeak");
        if (currentPassword === newPassword) errs.newPassword = t("passwordSameAsOld");
        if (!newPassword) errs.newPassword = t("fieldRequired");
        if (newPassword !== confirmPassword) errs.confirmPassword = t("passwordsDontMatch");
        return errs;
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        const validation = validatePassword();
        if (Object.keys(validation).length) return setPasswordErrors(validation);

        setLoading(true);
        UserService.changePassword(user._id, {
            oldPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
        })
            .then(() => {
                setNotificationMessage(t('newPasswordSet'));
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setTimeout(() => window.location.href = "/login", 3000);
                setTimeout(() => dispatch(loggOutUser()), 3000);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                setErrorResponse(true);
                setNotificationMessage(t('invalidCredentials'));
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setTimeout(() => setErrorResponse(false), 3000);
            });
    };

    const handleDeleteAccount = () => {
        setLoading(true);
        AdminService.deleteUserAndData(user._id)
            .then(() => {
                setLoading(false);
                dispatch(loggOutUser());
                navigate("/");
            })
            .catch(() => {
                setLoading(false);
            })
    };

    const handleSubscribe = (plan) => {
        setSelectedPlan(plan);
        setSubLoading(true);
        AdminService.createCheckoutSession({ priceId: plan.priceId, planType: plan.planType })
            .then((res) => {
                if (res.data.url) {
                    window.location.href = res.data.url;
                }
                else if (res.data.message) {
                    window.location.reload();
                }
                setSubLoading(false);
                //console.log(res);
            })
            .catch((err) => {
                setSubLoading(false);
                //console.log(err);
            })
    };

    const handleCancelSubscription = () => {
        setSubLoading(true);
        AdminService.cancelSubscription()
            .then((res) => {
                //console.log(res);
                setSubLoading(false);
                dispatch(refreshDataUserService());
            })
            .catch((err) => {
                setSubLoading(false);
                //console.log(err);
            })
    };

    const handleReactivateSubscription = () => {
        setSubLoading(true);
        AdminService.reactivateSubscription()
            .then((res) => {
                //console.log(res);
                setSubLoading(false);
                dispatch(refreshDataUserService());
            })
            .catch((err) => {
                setSubLoading(false);
                //console.log(err);
            })
    };

    const handleInvitationUpload = () => {
        if (!invitationFile) return;

        const formData = new FormData();
        formData.append('image', invitationFile);

        setUploadLoading(true);
        AdminService.uploadInvitation(wedding._id, formData)
            .then((res) => {
                const newUrl = res.data.invitationUrl;
                setInvitationPreview(newUrl);
                setInvitationFile(null);
                dispatch(refreshDataUserService());
                setUploadLoading(false);
                setNotificationMessage("Pozivnica uspješno uploadana!");
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
            })
            .catch(() => {
                setUploadLoading(false);
                setErrorResponse(true);
                setNotificationMessage("Greška pri uploadu.");
                setShowNotification(true);
                setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
            });
    };

    const handleInvitationDelete = () => {
        if (!wedding?.invitationUrl) {
            // Ako nema uploadane pozivnice, samo očisti lokalni preview (file picker state)
            setInvitationPreview(null);
            setInvitationFile(null);
            setShowDeleteInvitationModal(false);
            return;
        }

        setDeleteLoading(true);
        AdminService.deleteInvitation(wedding._id)
            .then(() => {
                setInvitationPreview(null);
                setInvitationFile(null);
                dispatch(refreshDataUserService());
                setDeleteLoading(false);
                setShowDeleteInvitationModal(false);
                setNotificationMessage("Pozivnica uspješno obrisana.");
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
            })
            .catch(() => {
                setDeleteLoading(false);
                setShowDeleteInvitationModal(false);
                setErrorResponse(true);
                setNotificationMessage("Greška pri brisanju pozivnice.");
                setShowNotification(true);
                setTimeout(() => {
                    setShowNotification(false);
                    setErrorResponse(false);
                }, 3000);
            });
    };

    const renderTabContent = () => {
        if (activeTab === "personal") {
            return (
                <div className="space-y-4">
                    {/* <InputField name="userName" label={t("username")} value={formData.userName} onChange={handleChange} error={errors.userName} /> */}
                    <InputField name="email" type="email" maxLength={100} label={t("email")} value={formData.email} onChange={handleChange} error={errors.email} />
                    <InputField name="phone" label={t("phone")} value={formData.phone} onChange={handleChange} error={errors.phone} placeholder={t("optional")} />
                    <ModalActions loading={loading} onCancel={onClose} onSave={handleSaveUser} saveLabel={t("saveChanges")} cancelLabel={t("cancel")} hasChanges={hasChanges} />
                </div>
            );
        }

        if (activeTab === "wedding") {
            return (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <InputField name="brideName" maxLength={50} label={t("brideName")} value={formData.brideName} onChange={handleChange} error={errors.brideName} />
                        <InputField name="groomName" maxLength={50} label={t("groomName")} value={formData.groomName} onChange={handleChange} error={errors.groomName} />
                    </div>
                    <InputField name="date" readOnly={formData.dateChangeCount >= 3} label={t("weddingDate")} type="date" value={formData.date.slice(0, 10)} onChange={handleChange} error={errors.date} />
                    <InputField name="location" readOnly={user.hasPremiumLocation
                    } maxLength={100} label={t("weddingLocation")} value={formData.location} onChange={handleChange} error={errors.location} />
                    <InputField name="city" readOnly={user.hasPremiumLocation} maxLength={100} label={t("weddingCity")} value={formData.city} onChange={handleChange} error={errors.city} />
                    <div className="relative" ref={lockReservationRef}>
                        <label className="flex items-center mb-1">{t("lockReservationLabel")}</label>
                        <div
                            tabIndex={0}
                            className={`w-full bg-white mb-1 border border-gray-700 flex flex-col justify-center items-start focus:outline-none rounded-xl cursor-pointer relative ${errors.lockReservationDaysBefore ? "border-red-500" : "border-gray-200"}`}
                            onClick={() => {
                                const newState = openDropdown === "lockReservation" ? null : "lockReservation";
                                setOpenDropdown(newState);
                                if (newState === "lockReservation") {
                                    setTimeout(scrollToDropdown, 100); // Mali delay da se dropdown prvo otvori
                                }
                            }}>
                            {formData.lockReservationDaysBefore !== undefined && formData.lockReservationDaysBefore !== null ? (
                                <span className="px-4 py-2">
                                    {t(daysOptions.find(option => option.value === formData.lockReservationDaysBefore)?.labelKey)}
                                    {formData.lockReservationDaysBefore === 3 && ` ⭐ ${t("recommended")}`}
                                </span>
                            ) : (
                                <label className="px-4 py-2 text-gray-800">{t("selectLockDays")}</label>
                            )}
                            {openDropdown === "lockReservation" ? (
                                <FaChevronUp className="text-xl absolute right-4" />
                            ) : (
                                <FaChevronDown className="text-xl absolute right-4" />
                            )}
                        </div>
                        <span className="text-sm text-gray-700">{t("lockReservationHelp")}</span>
                        {openDropdown === "lockReservation" && (
                            <div className=" flex flex-col w-full mt-1 mb-2 border border-gray-500 rounded-xl overflow-hidden bg-white">
                                {daysOptions.map(option => (
                                    <button
                                        key={option.labelKey}
                                        type="button"
                                        className="px-4 py-2 text-gray-800 hover:bg-gray-100 text-left"
                                        onClick={() => {
                                            setFormData({ ...formData, lockReservationDaysBefore: option.value });
                                            setOpenDropdown(null);
                                        }}>
                                        {t(option.labelKey)}
                                        {option.value === 3 && <span className="ml-2">⭐ {t("recommended")}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        {errors.lockReservationDaysBefore && <p className="text-red-500 text-sm mt-1">{errors.lockReservationDaysBefore}</p>}
                    </div>
                    <div>
                        <label className="font-medium flex items-center gap-2">
                            Poruka za goste
                        </label>
                        <textarea
                            name="inviteMessage"
                            value={formData.inviteMessage}
                            onChange={handleChange}
                            maxLength={500}
                            rows={4}
                            className="w-full bg-white border border-gray-700 px-4 py-2 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-400/20 rounded-xl resize-none"
                        />
                        <p className="text-sm text-gray-700">
                            Poruka se automatski kopira između pozdrava s imenom i prezimenom gosta i linka za rezervaciju.
                        </p>
                        <p className="text-sm text-gray-500 text-right">
                            {formData.inviteMessage?.length || 0}/500
                        </p>
                    </div>
                    <div className="flex items-center bg-white border border-gray-700 rounded-xl px-4 py-3">
                        <div>
                            <p className="font-medium text-gray-700">Gost može dodavati druge goste</p>
                            <p className="text-sm text-gray-500">
                                Gost može dodati druge goste s popisa i rezervirati stol zajedno s njima.
                                Opciju može iskoristiti samo jednom.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, allowGroupReservation: !prev.allowGroupReservation }))}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 ${formData.allowGroupReservation ? 'bg-rose-500' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${formData.allowGroupReservation ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                    <ModalActions loading={loading} onCancel={onClose} onSave={handleSaveWedding} saveLabel={t("saveChanges")} cancelLabel={t("cancel")} hasChanges={hasChanges} />
                </div>
            );
        }

        if (activeTab === "security") {
            return (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    {["currentPassword", "newPassword", "confirmPassword"].map((field) => (
                        <div key={field}>
                            <label className="font-medium text-sm text-gray-700">{t(field)}</label>
                            <div className="relative">
                                <input
                                    type={showPassword[field.replace("Password", "")] ? "text" : "password"}
                                    name={field}
                                    maxLength={50}
                                    value={passwordData[field]}
                                    onChange={handlePasswordChange}
                                    className={`inputWedding !pl-4 ${passwordErrors[field] && "!border-red-500"}`}
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                    onClick={() => setShowPassword((prev) => ({
                                        ...prev,
                                        [field.replace("Password", "")]: !prev[field.replace("Password", "")]
                                    }))}
                                >
                                    {!showPassword[field.replace("Password", "")] ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {passwordErrors[field] && <p className="text-red-500 text-sm mt-1">{passwordErrors[field]}</p>}
                        </div>
                    ))}
                    <ModalActions loading={loading} onCancel={onClose} onSave={null} saveLabel={t("changePassword")} cancelLabel={t("cancel")} isSubmit />
                    <hr className="" />
                    <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="bg-red-600 hover:bg-red-700 text-white h-12 px-4 py-2 rounded-xl flex items-center gap-2"
                    >
                        <FaTrash className="text-white" />
                        {t("deleteProfile")}
                    </button>
                </form>
            );
        }

        if (activeTab === "subscriptionn") {
            return (
                <div className="space-y-4">
                    {currentSubscription !== "free" ? (
                        <div className="p-4 border border-green-500 rounded-xl bg-green-50 text-green-800">
                            <h4 className="font-semibold">{t("activePlan")}: {plans.find((plan) => plan.planType === user?.planType).name}</h4>
                            <div className="mt-4 flex items-center justify-between">
                                {user?.cancelAtPeriodEnd ? (
                                    <p className="text-sm">
                                        {t("expiresOn")}: {new Date(user?.currentPeriodEnd).toLocaleDateString()}
                                    </p>
                                ) : (
                                    <p className="text-sm">
                                        Iduce plaćanje: {new Date(user?.currentPeriodEnd).toLocaleDateString()}
                                    </p>
                                )}
                                {user?.cancelAtPeriodEnd ? (
                                    <button
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full"
                                        onClick={() => handleReactivateSubscription()}
                                    >
                                        {t("cancelSubscription", "Zadrži pretplatu")}
                                    </button>
                                ) : (
                                    <button className=" text-red-600  px-4 py-2 rounded-full" onClick={() => handleCancelSubscription()}>{t("cancelSubscription", "Otkaži pretplatu")}</button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 border border-yellow-400 rounded-xl bg-yellow-50 text-yellow-800">
                            <p>{t("noActiveSubscription", "Trenutno nemate aktivnu pretplatu.")}</p>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4">
                        {plans.map((plan) => (
                            <div key={plan.planType} className={`border-2 rounded-2xl p-6 transition-all duration-300 shadow-md hover:shadow-xl border-blue-600 bg-white hover:border-blue-800`}>
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">{plan.name}</h3>
                                <p className="text-gray-600 mb-4">{plan.description}</p>
                                <p className="text-2xl font-bold text-blue-600 mb-4">{plan.price}</p>
                                <button className={`w-full py-2 rounded-xl text-white font-medium transition-colors ${user.planType !== plan.planType
                                    ? "bg-blue-600 hover:bg-blue-700"
                                    : "bg-gray-600 hover:bg-gray-700"}`}
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={subLoading || user?.planType === plan?.planType}>
                                    {subLoading && selectedPlan.planType === plan.planType
                                        ? t("processing", "Obrada...")
                                        : user.planType === plan.planType
                                            ? t("currentPlan", "Trenutni plan")
                                            : user.planType !== plan.planType ? t("subscribe", "Pretplati se") : t("changePlan", "Promjeni plan")}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="pt-2 flex justify-end">
                        <button
                            onClick={onClose}
                            className="border border-gray-500 px-4 py-2 rounded-xl text-gray-700 bg-white hover:bg-gray-100"
                        >
                            {t("close", "Zatvori")}
                        </button>
                    </div>
                </div >
            );
        }

        if (activeTab === "subscription") {
            return (
                <SubscriptionManagerOneTimePayment user={user} wedding={wedding} />
            );
        }

        if (activeTab === "invitation") {
            return (
                <div className="space-y-5">
                    {/* Status badge */}
                    <div className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl w-fit ${invitationPreview && !invitationFile
                        ? "bg-green-50 text-green-700 border border-green-300"
                        : "bg-yellow-50 text-yellow-700 border border-yellow-300"
                        }`}>
                        <span className={`w-2 h-2 rounded-full ${invitationPreview && !invitationFile ? "bg-green-500" : "bg-yellow-400"}`} />
                        {invitationPreview && !invitationFile ? "Pozivnica uploadana ✓" : "Pozivnica nije uploadana"}
                    </div>

                    {/* Thumbnail uploadane pozivnice */}
                    {invitationPreview && !invitationFile && (
                        <div className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-2xl shadow-sm">
                            <img
                                src={invitationPreview}
                                alt="Pozivnica"
                                className="w-16 h-16 rounded-xl object-cover border border-gray-200 cursor-pointer"
                                onClick={() => window.open(invitationPreview, "_blank")}
                            />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700">Trenutna pozivnica</p>
                                <button
                                    type="button"
                                    onClick={() => window.open(invitationPreview, "_blank")}
                                    className="text-xs text-blue-600 hover:underline"
                                >
                                    Prikaži u punoj veličini ↗
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowDeleteInvitationModal(true)}
                                disabled={deleteLoading}
                                className="flex items-center gap-1 text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded-lg hover:bg-red-50 disabled:opacity-50"
                            >
                                <FaTrash className="text-xs" /> Ukloni
                            </button>
                        </div>
                    )}

                    {/* File picker */}
                    <div>
                        <label className="font-medium text-gray-700 block mb-1">
                            {wedding?.invitationUrl ? "Zamijeni pozivnicu" : "Odaberi sliku pozivnice"}
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer w-full bg-white border border-gray-300 border-dashed hover:border-blue-400 hover:bg-blue-50 transition-colors px-4 py-3 rounded-xl text-sm text-gray-600">
                            <FaImage className="text-gray-400 text-lg shrink-0" />
                            <span className="truncate">
                                {invitationFile ? invitationFile.name : "Klikni za odabir slike (max 10MB)"}
                            </span>
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    setInvitationFile(file);
                                }}
                            />
                        </label>
                        {invitationFile && (
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                                Spreman za upload — još nije uploadano
                            </p>
                        )}
                    </div>

                    <ModalActions
                        loading={uploadLoading}
                        onCancel={onClose}
                        onSave={handleInvitationUpload}
                        saveLabel="Upload pozivnicu"
                        cancelLabel="Zatvori"
                        hasChanges={!!invitationFile}
                    />
                </div>
            );
        }
    };

    return (
        <div className="min-h-screen sm:py-8 fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center">
            {showNotification && <NotificationBanner message={notificationMessage} onClose={() => setShowNotification(false)} errorResponse={errorResponse} />}
            <div className="bg-gradient-to-br from-cyan-200 via-white to-pink-300 h-full w-full max-h-screen sm:max-w-3xl md:rounded-lg overflow-y-auto animate-fadeIn flex flex-col lg:flex-row">
                {/* Sidebar */}
                <div className="relative w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r-2 py-4 px-4 rounded-t-lg lg:rounded-l-lg">
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-700 hover:text-gray-800 text-2xl">
                        <FiX />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center ml-2 gap-2 mb-7">
                        <FaCog /> {t("settingsTitle")}
                    </h2>

                    <div className="relative">
                        <button onClick={() => setShowTabs(prev => !prev)} className="lg:hidden w-full border border-gray-500 focus:border-gray-700 rounded-lg px-3 py-2 text-left bg-white">
                            {t(`tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`)}
                            {showTabs ? <FaChevronUp className="absolute right-4 top-1/2 -translate-y-1/2" /> : <FaChevronDown className="absolute right-4 top-1/2 -translate-y-1/2" />}
                        </button>
                        {showTabs && (
                            <div className="absolute w-full mt-1 z-50 bg-white border border-gray-500 rounded-lg shadow-md overflow-hidden">
                                {["personal", wedding ? "wedding" : null, wedding ? "invitation" : null, wedding ? "subscription" : null, "security"].filter(Boolean).map(tab => (
                                    <button key={tab} onClick={() => { setActiveTab(tab); setShowTabs(false); setHasChanges(false); }} className="px-4 py-2 text-left w-full hover:bg-gray-200">
                                        {t(`tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:flex flex-col gap-2 mt-2">
                        {["personal", wedding ? "wedding" : null, wedding ? "invitation" : null, wedding ? "subscription" : null, "security"].filter(Boolean).map(tab => (
                            <button key={tab} onClick={() => { setActiveTab(tab); setHasChanges(false); }} className={`flex items-center text-left px-3 py-2 rounded ${activeTab === tab ? "bg-blue-700 text-white" : "hover:bg-gray-300"}`}>
                                {tab === "personal" && <FaUser className="mr-2" />}
                                {tab === "wedding" && <FaCalendarAlt className="mr-2" />}
                                {tab === "invitation" && <FaImage className="mr-2" />}
                                {tab === "subscription" && <FaCreditCard className="mr-2" />}
                                {tab === "security" && <FaLock className="mr-2" />}
                                {t(`tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-3/4 p-4 relative">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        {t(`tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`)}
                    </h3>
                    {renderTabContent()}
                </div>
            </div>
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("confirmDeleteTitle")}</h3>
                        <p className="text-sm text-gray-700 mb-6">{t("confirmDeleteMessage")}</p>
                        <div className="flex justify-end items-center gap-3">
                            <button
                                className="px-4 py-2 h-12 rounded-xl border border-gray-300 hover:bg-gray-100"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={loading}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                className="px-4 py-2 h-12 rounded-xl bg-red-600 text-white hover:bg-red-700 w-[40%] flex items-center justify-center"
                                onClick={() => handleDeleteAccount()}
                                disabled={loading}
                            >
                                {loading ? <LoadingForButton /> : t("confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showDeleteInvitationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center p-4">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Obriši pozivnicu?</h3>
                        <p className="text-sm text-gray-700 mb-6">
                            Pozivnica će biti trajno obrisana. Možeš uploadati novu kad god želiš.
                        </p>
                        <div className="flex justify-end items-center gap-3">
                            <button
                                className="px-4 py-2 h-12 rounded-xl border border-gray-300 hover:bg-gray-100"
                                onClick={() => setShowDeleteInvitationModal(false)}
                                disabled={deleteLoading}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                className="px-4 py-2 h-12 rounded-xl bg-red-600 text-white hover:bg-red-700 w-[40%] flex items-center justify-center disabled:opacity-60"
                                onClick={handleInvitationDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? <LoadingForButton /> : t("confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const InputField = ({ name, label, type = "text", value, onChange, error, placeholder, readOnly }) => (
    <div>
        <label className="font-medium text-gray-700 flex items-center gap-2">{label}</label>
        <input
            name={name}
            type={type}
            value={value}
            readOnly={readOnly}
            onChange={onChange}
            placeholder={placeholder}
            maxLength={50}
            className={`w-full bg-white border border-gray-700 px-4 py-2 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-400/20 rounded-xl ${error && "!border-red-500"} ${name !== "email" && name !== "userName" && "capitalize"}`}
        />
        {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
);

const ModalActions = ({ loading, onCancel, onSave, hasChanges = true, saveLabel = null, cancelLabel = null, isSubmit = false, disabled }) => (
    <div className="pt-2 flex justify-end gap-2 pb-10">
        <button
            onClick={onCancel}
            type="button"
            disabled={loading}
            className="border border-gray-500 px-4 py-2 rounded-xl text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed">
            {cancelLabel || "Odustani"}
        </button>
        <button
            type={isSubmit ? "submit" : "button"}
            onClick={onSave}
            disabled={loading || disabled || (!hasChanges && !isSubmit)}
            className={`submitButton w-full ${(hasChanges || isSubmit) && !loading ? "" : "!from-gray-400 !to-gray-500 cursor-not-allowed"}`}>
            {loading ? <LoadingForButton /> : saveLabel || "Spremi promjene"}
        </button>
    </div>
);

export default SettingsModal;
